<template>
    <el-dialog
        class="app-update-dialog im-dialog-shell im-dialog-shell--md im-dialog-shell--mobile-center"
        :visible.sync="visible"
        width="520px"
        :append-to-body="true"
        :close-on-click-modal="false"
    >
        <template #title>
            <div class="dialog-title">
                <span class="dialog-badge">
                    <i class="el-icon-refresh"></i>
                </span>
                <div class="dialog-heading">
                    <span class="dialog-caption">应用更新</span>
                    <strong class="dialog-name">{{ dialogTitle }}</strong>
                </div>
            </div>
        </template>

        <div class="dialog-body">
            <div class="version-strip">
                <div class="version-card">
                    <span class="version-label">当前版本</span>
                    <strong class="version-value">v{{ currentVersionDisplay }}</strong>
                </div>
                <span class="version-arrow">
                    <i class="el-icon-right"></i>
                </span>
                <div class="version-card version-card--highlight">
                    <span class="version-label">新版本</span>
                    <strong class="version-value">v{{ latestVersionDisplay }}</strong>
                </div>
            </div>

            <div class="guide-card">
                <div class="guide-title">{{ guideTitle }}</div>
                <p class="guide-text">{{ guideText }}</p>
                <div v-if="formattedReleaseDate" class="guide-meta">
                    发布时间 {{ formattedReleaseDate }}
                </div>
            </div>

            <div v-if="releaseNotes" class="notes-card">
                <div class="notes-title">更新说明</div>
                <p class="notes-content">{{ releaseNotes }}</p>
            </div>

            <el-alert
                v-if="showError"
                :title="updater.errorMessage || '应用更新失败，请稍后重试'"
                type="error"
                :closable="false"
                show-icon
            />

            <div v-if="showProgress" class="progress-card">
                <div class="progress-head">
                    <span>下载进度</span>
                    <span>{{ progressText }}</span>
                </div>
                <el-progress :percentage="progressPercent"></el-progress>
            </div>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button @click="visible = false">稍后</el-button>
            <el-button
                v-if="showDownloadButton"
                type="primary"
                :loading="updater.downloading || downloadSubmitting"
                :disabled="updater.downloading || downloadSubmitting"
                @click="handleDownload"
            >{{ downloadButtonText }}</el-button>
            <el-button
                v-if="showInstallButton"
                type="primary"
                :loading="updater.installing || installSubmitting"
                :disabled="updater.installing || installSubmitting"
                @click="handleInstall"
            >{{ installButtonText }}</el-button>
        </span>
    </el-dialog>
</template>

<script>
export default {
    name: 'AppUpdateDialog',
    data() {
        return {
            visible: false,
            lastAutoPromptKey: '',
            downloadSubmitting: false,
            installSubmitting: false
        };
    },
    computed: {
        updater() {
            return this.configStore && this.configStore.updater ? this.configStore.updater : {};
        },
        currentVersionDisplay() {
            return this.updater.currentVersion || '未知';
        },
        latestVersionDisplay() {
            return this.updater.latestVersion || this.updater.currentVersion || '未知';
        },
        releaseNotes() {
            return String(this.updater.releaseNotes || '').trim();
        },
        progressPercent() {
            return Math.max(0, Math.min(100, Number(this.updater.progressPercent || 0)));
        },
        progressText() {
            return `${this.progressPercent.toFixed(0)}%`;
        },
        showProgress() {
            return this.updater.phase === 'downloading';
        },
        showDownloadButton() {
            return (
                (this.updater.phase === 'available' || (this.updater.phase === 'error' && Boolean(this.updater.latestVersion)))
                && !this.updater.downloading
                && !this.updater.downloaded
            );
        },
        showInstallButton() {
            return this.updater.phase === 'downloaded' || this.updater.phase === 'installing' || Boolean(this.updater.downloaded);
        },
        showError() {
            return this.visible && this.updater.phase === 'error' && Boolean(this.updater.errorMessage);
        },
        downloadButtonText() {
            return this.updater.phase === 'error' ? '重新更新' : '立即更新';
        },
        installButtonText() {
            return this.updater.installing || this.updater.phase === 'installing' ? '正在安装' : '立即安装';
        },
        dialogTitle() {
            if (this.showError) {
                return '应用更新遇到问题';
            }
            if (this.updater.phase === 'installing') {
                return '正在安装新版本';
            }
            if (this.showInstallButton) {
                return '可以安装新版本';
            }
            if (this.showProgress) {
                return '正在准备新版本';
            }
            return '发现新版本';
        },
        guideTitle() {
            if (this.showError) {
                return '可以稍后重新尝试';
            }
            if (this.updater.phase === 'installing') {
                return '正在关闭实例并安装';
            }
            if (this.showInstallButton) {
                return '新版本已准备就绪';
            }
            if (this.showProgress) {
                return '正在下载并准备安装包';
            }
            return '建议在空闲时更新应用';
        },
        guideText() {
            if (this.showError) {
                return '更新过程中出现了异常。你可以直接重新更新，或暂时稍后处理。';
            }
            if (this.updater.phase === 'installing') {
                return '安装器将关闭所有运行中的应用实例，并在完成后重启应用。';
            }
            if (this.showInstallButton) {
                return '点击“立即安装”后，应用会关闭所有运行中的实例并完成安装。';
            }
            if (this.showProgress) {
                return '新版本正在后台下载，下载完成后会继续提示你安装。';
            }
            return '点击“立即更新”开始下载新版本。下载完成后，你可以一键安装，应用会自动重启。';
        },
        formattedReleaseDate() {
            const raw = this.updater.releaseDate;
            if (!raw) {
                return '';
            }
            const date = new Date(raw);
            if (Number.isNaN(date.getTime())) {
                return '';
            }
            const year = date.getFullYear();
            const month = `${date.getMonth() + 1}`.padStart(2, '0');
            const day = `${date.getDate()}`.padStart(2, '0');
            const hour = `${date.getHours()}`.padStart(2, '0');
            const minute = `${date.getMinutes()}`.padStart(2, '0');
            return `${year}-${month}-${day} ${hour}:${minute}`;
        }
    },
    watch: {
        'configStore.updater.phase': {
            handler(nextPhase, previousPhase) {
                const updater = this.updater || {};
                if (nextPhase === 'available') {
                    if (updater.trigger === 'manual') {
                        this.visible = true;
                        return;
                    }
                    const autoKey = `${updater.tenantKey || ''}:${updater.latestVersion || ''}`;
                    if (!autoKey || autoKey === this.lastAutoPromptKey) {
                        return;
                    }
                    this.lastAutoPromptKey = autoKey;
                    this.visible = true;
                    return;
                }
                if (nextPhase === 'downloaded') {
                    this.visible = true;
                    return;
                }
                if (nextPhase === 'installing') {
                    this.visible = true;
                    return;
                }
                if (nextPhase === 'error' && previousPhase === 'downloading') {
                    this.visible = true;
                }
            }
        }
    },
    methods: {
        handleDownload() {
            if (!this.$desktopUpdater || this.downloadSubmitting || this.updater.downloading) {
                return;
            }
            this.downloadSubmitting = true;
            this.$desktopUpdater.startUpdate().catch((error) => {
                this.$message.error(error && error.message ? error.message : '下载应用更新失败');
            }).finally(() => {
                this.downloadSubmitting = false;
            });
        },
        handleInstall() {
            if (!this.$desktopUpdater || this.installSubmitting || this.updater.installing) {
                return;
            }
            this.installSubmitting = true;
            this.$desktopUpdater.installUpdate().then((success) => {
                if (success === false) {
                    this.$message.error('安装应用更新失败');
                }
            }).catch((error) => {
                this.$message.error(error && error.message ? error.message : '安装应用更新失败');
            }).finally(() => {
                this.installSubmitting = false;
            });
        }
    }
};
</script>

<style scoped lang="scss">
.app-update-dialog {
    .dialog-title {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .dialog-badge {
        width: 40px;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 14px;
        color: var(--im-color-primary);
        background: rgba(var(--im-primary-rgb), 0.08);
        border: 1px solid rgba(var(--im-primary-rgb), 0.14);

        i {
            font-size: 18px;
        }
    }

    .dialog-heading {
        min-width: 0;
        display: grid;
        gap: 4px;
    }

    .dialog-caption {
        font-size: 12px;
        color: var(--im-text-color-light);
    }

    .dialog-name {
        font-size: 18px;
        line-height: 1.2;
        color: var(--im-text-color);
    }

    .dialog-body {
        display: grid;
        gap: 14px;
    }

    .version-strip {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        gap: 10px;
        align-items: center;
    }

    .version-card {
        display: grid;
        gap: 6px;
        padding: 14px 16px;
        border-radius: 16px;
        border: 1px solid rgba(var(--im-black-rgb), 0.06);
        background: rgba(var(--im-black-rgb), 0.022);
    }

    .version-card--highlight {
        background: rgba(var(--im-primary-rgb), 0.08);
        border-color: rgba(var(--im-primary-rgb), 0.14);
    }

    .version-label {
        font-size: 12px;
        color: var(--im-text-color-light);
    }

    .version-value {
        font-size: 18px;
        line-height: 1.2;
        color: var(--im-text-color);
    }

    .version-arrow {
        width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        color: var(--im-color-primary);
        background: rgba(var(--im-primary-rgb), 0.08);
    }

    .guide-card,
    .notes-card,
    .progress-card {
        display: grid;
        gap: 8px;
        padding: 14px 16px;
        border-radius: 16px;
        background: rgba(var(--im-black-rgb), 0.02);
        border: 1px solid rgba(var(--im-black-rgb), 0.05);
    }

    .guide-title,
    .notes-title {
        font-size: 14px;
        font-weight: 600;
        color: var(--im-text-color);
    }

    .guide-text,
    .notes-content {
        margin: 0;
        font-size: 13px;
        line-height: 1.7;
        color: var(--im-text-color-light);
        white-space: pre-wrap;
    }

    .guide-meta {
        font-size: 12px;
        color: var(--im-text-color-lighter);
    }

    .progress-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        font-size: 13px;
        color: var(--im-text-color-light);
    }

    .dialog-footer {
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

    @media (max-width: 767px) {
        .version-strip {
            grid-template-columns: 1fr;
        }

        .version-arrow {
            margin: 0 auto;
            transform: rotate(90deg);
        }
    }
}
</style>
